import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useSingleUserControl } from "../../../context/SuperAdmin/SingleUserControlContext";
import { Controller, useForm } from "react-hook-form";
import { PutUserPassword } from "../../../interfaces/SuperAdmin/Others/CognitoUser";
import { Visibility, VisibilityOff } from "@mui/icons-material";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const PasswordModal: FC<Props> = ({ isOpen, setIsOpen }) => {
  const { isChangingPass, rdsUser, cognitoCredentialsUser, changePassword } =
    useSingleUserControl();

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<PutUserPassword>();

  const [showPassword, setShowPassword] = useState(false);

  // Función para manejar el envío del formulario
  const handleSubmitForm = (data: PutUserPassword) => {
    if (cognitoCredentialsUser) {
      console.log(cognitoCredentialsUser.username, data);
      changePassword(cognitoCredentialsUser.username, data);
    }
  };

  const handleClose = () => {
    if (!isChangingPass) {
      setIsOpen(false);
    }
  };

  // Cerramos el popup cuando se ejecute la función
  useEffect(() => {
    if (!isChangingPass && isOpen) {
      handleClose();
    }
  }, [isChangingPass]);

  useEffect(() => {
    if (rdsUser) {
      reset({
        version: rdsUser.version,
        password: "",
      });
    }
  }, [rdsUser]);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0, 0, 0, 0.3)", // Fondo negro con opacidad
      }}
    >
      <Box
        component="form"
        sx={{
          width: 400,
          bgcolor: "background.paper", // Fondo blanco del modal
          borderRadius: 2, // Bordes redondeados
          p: 4,
          boxShadow: 24,
          minWidth: "600px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        <Typography
          id="edit-subscription-modal-title"
          variant="h6"
          component="h2"
        >
          Cambiar contraseña
        </Typography>

        {/* Campo Contraseña */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Contraseña
          </Typography>
          <Controller
            name="password"
            control={control}
            rules={{
              required: "La contraseña es obligatoria",
              minLength: {
                value: 8,
                message: "La contraseña debe tener al menos 8 caracteres",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                type={showPassword ? "text" : "password"}
                fullWidth
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={isChangingPass}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* Lista de requisitos */}
          <Box sx={{ mt: -1, mb: 1 }}>
            <Typography variant="caption" color="textSecondary">
              • Debe tener al menos 8 caracteres
            </Typography>
          </Box>
        </Box>

        {/* Campo oculto para version */}
        <input type="hidden" {...register("version")} />

        {/* Botones */}
        <Box
          sx={{
            textAlign: "right",
            display: "flex",
            justifyContent: "flex-end",
            gap: 3,
            mt: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setIsOpen(false)}
            disabled={isChangingPass}
            sx={{
              borderRadius: 14,
              border: "1px solid #336B87",
              color: "#336B87",
              "&:hover": {
                backgroundColor: "#336B87",
                color: "#EEF3F6",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            sx={{ background: "#336B87" }}
            disabled={isChangingPass}
            type="submit"
          >
            {isChangingPass ? "Guardando" : "Guardar"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PasswordModal;
